import {
  ItemTypeConsts,
  StoreModuleMap,
} from 'client/util/Constants';
import {
  assign,
  get,
} from 'lodash';
import FeatureTrialsApi from 'api/v2/FeatureTrialsApi';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import MspState from 'client/components/models/MspState';
import OrganizationsApi from 'api/v2/OrganizationsApi';
import Vue from 'vue';
import daysLeftInFairUsePeriod from 'client/components/Trials/utils/daysLeftInFairUsePeriod';

import {
  TrialFeatures,
} from 'client/util/Constants/FeatureTrialConstants';

const settingsModule = StoreModuleMap[ItemTypeConsts.settings];

const putOrgGrowthData = (orgId, growthData) => OrganizationsApi
  .updateOrganizationSettings(orgId, { settings: { growthData } });

const isActiveFeatureTrial = ({ startDate, endDate }) => !!startDate && !endDate;

// --------------------------------------------------------------------------
// Actions
// --------------------------------------------------------------------------
async function fetchFeatureUsage({ commit }, { featureCode }) {
  const featureUsage = await FeatureTrialsApi.fetch(featureCode);
  commit('SET_FEATURE_USAGE', { featureCode, featureUsage: featureUsage.data });
}

async function fetchCurrentOrgEntitlement({ commit }, orgId) {
  const responseData = await OrganizationsApi.getOrganizationDetails(orgId, 'entitlement');
  const { entitlement } = responseData;

  commit('SET_ORG_ENTITLEMENT_DATA', entitlement);
}

function setOrgGrowthData({ commit, rootState }, { growthData, pushChanges = true }) {
  commit('SET_ORG_FEATURE_TRIAL_DATA', { growthData });

  if (pushChanges) {
    const { orgId } = rootState[settingsModule];
    putOrgGrowthData(orgId, growthData);
  }
}

// --------------------------------------------------------------------------
// Getters
// --------------------------------------------------------------------------

function getAllActiveFeatureTrials(state) {
  const features = get(state, 'orgGrowthData.featureUsage', {});
  const filteredFeatures = Object.keys(features).filter((feature) => {
    const { startDate, endDate } = features[feature];
    return isActiveFeatureTrial({ startDate, endDate });
  });
  return filteredFeatures;
}

function getIsNudgeDue() {
  return (startDateIsoString) => {
    const daysLeftToShowNudge = 15;
    const daysLeftInFairUse = daysLeftInFairUsePeriod(startDateIsoString);
    return daysLeftInFairUse <= daysLeftToShowNudge;
  };
}

function getFeatureUsage(state) {
  return ((feature) => {
    const features = get(state, 'orgGrowthData.featureUsage', {});
    return features[feature];
  });
}

function getUpgradeNudgeVisibility(state, getters) {
  return (feature) => {
    const trialFeatureData = getters.getFeatureUsage(feature);

    if (!trialFeatureData || trialFeatureData.endDate || MspState.isAnMspOrChild) {
      return false;
    }

    return getters.getIsNudgeDue(trialFeatureData.startDate);
  };
}

function getPremiumBadgeProps(state, getters, rootState, rootGetters) {
  return (featureCode, metaData = {}) => {
    const { defaultPremiumBadgeVisibility, ignoreTrial } = metaData;
    const featureUsage = getters.getFeatureUsage(featureCode) || {};
    const showFeatureUsage = (!ignoreTrial && isActiveFeatureTrial(featureUsage)) || false;
    const { isAnMspOrChild } = MspState;
    const hasFeature = rootGetters['PlanModule/planHasFeature'](featureCode);
    const isCustomer = rootGetters['SettingsModule/getIsCustomer'];

    const visible = (defaultPremiumBadgeVisibility || showFeatureUsage)
      && isCustomer
      && !isAnMspOrChild
      && !hasFeature;

    const nonHeaderBadgeVisible = !showFeatureUsage
      && isCustomer
      && !isAnMspOrChild
      && !hasFeature;

    return {
      visible,
      nonHeaderBadgeVisibility: nonHeaderBadgeVisible,
      showFeatureUsage,
    };
  };
}

// --------------------------------------------------------------------------
// Mutations
// --------------------------------------------------------------------------

/**
 * Sets the org growthData object to the state
 * @param {Object} growthData - the org growthData object
 */
function SET_ORG_FEATURE_TRIAL_DATA(state, { growthData, maintainPreviousObject = true }) {
  if (maintainPreviousObject) {
    const newOrgGrowthObject = assign({}, state.orgGrowthData, growthData);
    Vue.set(state, 'orgGrowthData', newOrgGrowthObject);
  } else {
    Vue.set(state, 'orgGrowthData', growthData);
  }
  Vue.set(state, 'trialDataLoaded', true);
}

function SET_ORG_ENTITLEMENT_DATA(state, entitlement) {
  Vue.set(state, 'entitlement', entitlement);
}

function SET_FEATURE_USAGE(state, { featureCode, featureUsage }) {
  const currentFeatures = get(state, 'orgGrowthData.featureUsage', {});
  const newFeatureUsage = {
    featureUsage: {
      ...currentFeatures,
      [featureCode]: featureUsage,
    },
  };
  const currentUsage = currentFeatures[featureCode];
  if (!currentUsage || !currentUsage.startDate) {
    if (featureUsage.startDate && !featureUsage.endDate) {
      ModalEventBus.open({
        component: 'TrialModal',
        componentProps: TrialFeatures[featureCode],
        isLargeModal: true,
      });
    }
  }
  const newOrgGrowthData = assign({}, state.orgGrowthData, newFeatureUsage);

  Vue.set(state, 'orgGrowthData', newOrgGrowthData);
}

// --------------------------------------------------------------------------
// Private functions
// --------------------------------------------------------------------------

export const createFeatureTrialModule = () => ({
  namespaced: true,
  state: {
    orgGrowthData: {
    },
    entitlement: {},
  },
  getters: {
    getAllActiveFeatureTrials,
    getFeatureUsage,
    getPremiumBadgeProps,
    getIsNudgeDue,
    getUpgradeNudgeVisibility,
  },
  actions: {
    fetchCurrentOrgEntitlement,
    fetchFeatureUsage,
    setOrgGrowthData,
  },
  mutations: {
    SET_ORG_ENTITLEMENT_DATA,
    SET_ORG_FEATURE_TRIAL_DATA,
    SET_FEATURE_USAGE,
  },
});

export default createFeatureTrialModule();
